/** A civil date, not an instant. Stored and transported as YYYY-MM-DD. */
export type CalendarDate = string;

const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isCalendarDate(value: string): value is CalendarDate {
  return CALENDAR_DATE_RE.test(value);
}

function asDate(instant: Date | string): Date {
  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) {
    throw new RangeError("Invalid instant");
  }
  return d;
}

/** UTC calendar date of an instant. For DynamoDB day keys (T12). */
export function utcCalendarDate(instant: Date | string): CalendarDate {
  return asDate(instant).toISOString().slice(0, 10);
}

/** Local calendar date of an instant in an IANA zone. */
export function localCalendarDate(
  instant: Date | string,
  zone: string
): CalendarDate {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(asDate(instant));
}

/**
 * Display timezone (T5). Optional record.timezone is reserved for a future
 * owner-zone policy; no domain record sets it today.
 */
export function resolveDisplayZone(record?: { timezone?: string }): string {
  if (record?.timezone) return record.timezone;
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export type InstantFormat = "date" | "dateTime" | "monthYear";
export type ZonedBucketUnit = "day" | "hour";

export function formatInstant(
  instant: Date | string,
  zone: string,
  options?: { format?: InstantFormat; locale?: string }
): string {
  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) return "";
  const format = options?.format ?? "date";
  if (format === "monthYear") {
    return d.toLocaleString(options?.locale, {
      month: "long",
      year: "numeric",
      timeZone: zone,
    });
  }
  if (format === "dateTime") {
    return d.toLocaleString(options?.locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: zone,
    });
  }
  return d.toLocaleDateString(options?.locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: zone,
  });
}

/**
 * Format a CalendarDate so it renders as itself in every zone (T2).
 * Parses as UTC midnight, formats with timeZone UTC. Do not pass an instant.
 */
export function formatCalendarDate(
  date: CalendarDate,
  options?: { locale?: string }
): string {
  if (!isCalendarDate(date)) return date;
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(options?.locale ?? "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

const pad2 = (n: number) => String(n).padStart(2, "0");

interface CivilParts {
  year: number;
  month: number;
  day: number;
  hour: number;
}

/**
 * Wall-clock parts of an instant in an IANA zone. Hour "24" (midnight on some
 * ICU builds) is rolled to 00:00 on the next civil day so the key is stable.
 */
function civilParts(instant: Date, zone: string): CivilParts {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
    hour12: false,
  });
  const map: Record<string, string> = {};
  for (const part of fmt.formatToParts(instant)) {
    if (part.type !== "literal") map[part.type] = part.value;
  }
  let year = Number(map.year);
  let month = Number(map.month);
  let day = Number(map.day);
  let hour = Number(map.hour);
  if (hour === 24) {
    hour = 0;
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    year = next.getUTCFullYear();
    month = next.getUTCMonth() + 1;
    day = next.getUTCDate();
  }
  return { year, month, day, hour };
}

function formatDayKey(parts: CivilParts): string {
  return `${pad2(parts.year)}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

function formatHourKey(parts: CivilParts): string {
  return `${formatDayKey(parts)}T${pad2(parts.hour)}`;
}

/** Previous civil day, via UTC date math so DST cannot skip a label. */
function previousDay(parts: CivilParts): CivilParts {
  const t = new Date(Date.UTC(parts.year, parts.month - 1, parts.day - 1));
  return {
    year: t.getUTCFullYear(),
    month: t.getUTCMonth() + 1,
    day: t.getUTCDate(),
    hour: 0,
  };
}

/** Previous civil hour. Consecutive labels, including hours a DST jump skips. */
function previousHour(parts: CivilParts): CivilParts {
  const t = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour - 1));
  return {
    year: t.getUTCFullYear(),
    month: t.getUTCMonth() + 1,
    day: t.getUTCDate(),
    hour: t.getUTCHours(),
  };
}

/**
 * Bucket key for an instant in an IANA zone (SIGNUP_ABUSE_DEFENSE_PLAN.md D-15).
 * Day keys are YYYY-MM-DD; hour keys are YYYY-MM-DDTHH (00-23).
 */
export function zonedBucketKey(instant: string, zone: string, unit: ZonedBucketUnit): string {
  const parts = civilParts(asDate(instant), zone);
  return unit === "hour" ? formatHourKey(parts) : formatDayKey(parts);
}

/**
 * `count` bucket keys ending at the bucket that contains `end`, oldest first.
 * Keys are civil labels in `zone`, not UTC dates.
 */
export function zonedBucketRange(
  end: Date,
  count: number,
  zone: string,
  unit: ZonedBucketUnit
): string[] {
  if (count < 1) return [];
  const keys: string[] = [];
  let parts = civilParts(asDate(end), zone);
  for (let i = 0; i < count; i++) {
    keys.push(unit === "hour" ? formatHourKey(parts) : formatDayKey(parts));
    parts = unit === "hour" ? previousHour(parts) : previousDay(parts);
  }
  keys.reverse();
  return keys;
}
